import React from 'react';
import clsx from 'clsx';
import {
	ListItem,
	ListItemAvatar,
	type ListItemProps,
	ListItemSecondaryAction,
	ListItemText,
} from '@mui/material';
import {ColoredAvatar} from '@netx/core-components/ColoredAvatar';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {
	getUserGroupListItemUtilityClass,
	USER_GROUP_LIST_ITEM_CLASS_NAME,
	type UserGroupListItemClasses,
	userGroupListItemClasses,
} from './userGroupListItemClasses';

type UserGroupListItemOwnerState = WithClasses<
	UserGroupListItemClasses,
	{
		hasSecondaryLabel?: boolean;
		isActive?: boolean;
	}
>;

const useUtilityClasses = (state: UserGroupListItemOwnerState) => {
	const {classes, hasSecondaryLabel, isActive} = state;
	const slots = {
		root: [
			'root',
			isActive && 'isActive',
			hasSecondaryLabel && 'hasSecondaryLabel',
		],
	};
	return composeClasses(slots, getUserGroupListItemUtilityClass, classes);
};

const UserGroupListItemRoot = styled(ListItem, {
	name: USER_GROUP_LIST_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<UserGroupListItemOwnerState>) => ({
	...(ownerState?.hasSecondaryLabel ? {minHeight: 56} : {}),
	[`&.${userGroupListItemClasses.isActive}`]: {
		backgroundColor: theme.palette.action.hover,
	},
}));

export interface UserGroupListItemProps
	extends Omit<ListItemProps, 'classes' | 'sx'>,
		WithClasses<UserGroupListItemClasses> {
	isActive?: boolean;
	label: string;
	secondaryLabel?: string;
	secondaryAction?: React.ReactNode;
}

export const UserGroupListItemPrimitive = (
	{
		classes: _classes,
		className,
		isActive,
		label,
		secondaryLabel,
		secondaryAction,
		...props
	}: UserGroupListItemProps,
	ref: React.ForwardedRef<HTMLLIElement>,
) => {
	const ownerState: UserGroupListItemOwnerState = {
		classes: _classes,
		className,
		hasSecondaryLabel: secondaryLabel != null,
		isActive,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<UserGroupListItemRoot
			className={clsx(classes.root, className)}
			ref={ref}
			secondaryAction={secondaryAction}
			// TODO: this logic will be deprecated in mui v6
			// since our component has a secondary action it will
			// get wrapped in a container component
			// need to investigate how this behavior will change -
			// personally the un-implicit wrapping of this is not a
			// good thing on their end
			// ContainerComponent={secondaryAction != null ? 'div' : undefined}
			{...props}
			ownerState={ownerState}
		>
			<ListItemAvatar>
				<ColoredAvatar label={label} />
			</ListItemAvatar>
			<ListItemText primary={label} secondary={secondaryLabel} />
			{/* {secondaryAction && (
				<ListItemSecondaryAction>{secondaryAction}</ListItemSecondaryAction>
			)} */}
		</UserGroupListItemRoot>
	);
};

export const UserGroupListItem = React.forwardRef(UserGroupListItemPrimitive);

export default UserGroupListItem;
